import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { dom, library } from '@fortawesome/fontawesome-svg-core';
import {
  faExchange,
  faEmptySet,
  faSquare,
} from '@fortawesome/pro-solid-svg-icons';

import { svgWithJs, variables } from '../variables';

// Note: Your element must have a hyphen in the name (for example, "hello-world"). It's a requirement
// so that our components don't collide with future additions to HTML.
@customElement('apt-data-summary')
export class DataSummary extends LitElement {
  @property({ type: String })
  color = `default`;

  @property({ type: Boolean })
  filtered = false;

  @property({ type: String })
  icon = 'fa-empty-set';

  @property({ type: Number })
  partial = 0;

  @property({ type: Number })
  total = 0;

  @property({ type: String })
  label = '';

  static override get styles() {
    return [
      variables,
      svgWithJs,
      css`
        :host {
          padding: 1em 0.5em;
          line-height: 1;
        }

        .tooltip-filtered {
          margin-left: 5px;
          margin-bottom: 3px;

          text-align: left;
          color: $gray-400;
        }

        .tooltip-partial {
          font-size: large;
        }

        .percentage {
          font-size: 12px;
          color: #545454;
          font-weight: normal;
        }

        .tooltip-wrapper {
          width: fit-content;
          display: flex;
          text-align: left;
        }
      `,
    ];
  }

  getPercentage(numerator: number, decominator: number) {
    const percentage = Math.round((numerator / decominator) * 100);
    if (Number.isNaN(percentage)) {
      return 0;
    }
    return percentage;
  }

  override render() {
    return html`
      ${this.filtered
        ? html`<div class="tooltip-filtered">Showing Filtered</div>`
        : null}
      <div class="tooltip-wrapper">
        <div>
          <span
            class="fa-stack"
            style="font-size: 13px; color: var(--${this.color})"
          >
            <i class="fas fa-square fa-stack-2x"></i>
            <i class="fas fa-stack-1x fa-inverse ${this.icon}"></i>
          </span>
        </div>
        <div class="data-summary">
          <div class="tooltip-partial">
            <strong>${this.partial}</strong> of <strong>${this.total}</strong>
          </div>
          <div class="percentage" data-test-selector="percentage-label">
            ${this.getPercentage(this.partial, this.total)}% of ${this.label}
          </div>
        </div>
      </div>
    `;
  }

  protected override firstUpdated(): void {
    library.add(faExchange, faEmptySet, faSquare);
    // @ts-ignore The dom.watch() TS definition indicates no parameters for this. However, when
    // you actually dig into the code, you see that it looks at the arguments after it gets into
    // the code and tries to pull some out if there aren't any (it even supplies an empty object
    // for options if you don't supply one yourself).
    //
    // Note: We have to wait for the first update for the shadow DOM to even be created.
    dom.watch({
      autoReplaceSvgRoot: this.shadowRoot,
      observeMutationsRoot: this.shadowRoot,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'apt-data-summary': DataSummary;
  }
}
